import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Alert,
  Tooltip,
  Switch,
  FormControlLabel,
  Snackbar,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Link as LinkIcon,
  ContentCopy as CopyIcon,
  Key as KeyIcon,
} from '@mui/icons-material';
import { adminAPI, User, UserCreate, UserUpdate } from '../services/adminApi';

const AdminUsersPage: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteUrl, setInviteUrl] = useState('');
  const [inviteExpires, setInviteExpires] = useState('');

  // Form data
  const [formData, setFormData] = useState<{
    username: string;
    email: string;
    password: string;
    role: 'admin' | 'user';
    is_active: boolean;
    expiration_date: string;
  }>({
    username: '',
    email: '',
    password: '',
    role: 'user',
    is_active: true,
    expiration_date: '',
  });

  // Snackbar
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({ open: false, message: '', severity: 'success' });

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await adminAPI.listUsers(page + 1, rowsPerPage);
      setUsers(response.users);
      setTotal(response.total);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } };
      setError(error.response?.data?.detail || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleOpenDialog = (user?: User) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        username: user.username,
        email: user.email,
        password: '',
        role: user.role,
        is_active: user.is_active,
        expiration_date: user.expiration_date
          ? new Date(user.expiration_date).toISOString().slice(0, 16)
          : '',
      });
    } else {
      setEditingUser(null);
      setFormData({
        username: '',
        email: '',
        password: '',
        role: 'user',
        is_active: true,
        expiration_date: '',
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingUser(null);
    setError(null);
  };

  const handleSaveUser = async () => {
    if (!editingUser && formData.password && formData.password.length < 12) {
      setError('Password must be at least 12 characters');
      return;
    }
    try {
      setError(null);
      if (editingUser) {
        const updateData: UserUpdate = {
          email: formData.email,
          role: formData.role,
          is_active: formData.is_active,
          expiration_date: formData.expiration_date || null,
        };
        await adminAPI.updateUser(editingUser.id, updateData);
        setSnackbar({
          open: true,
          message: 'User updated successfully',
          severity: 'success',
        });
      } else {
        const createData: UserCreate = {
          username: formData.username,
          email: formData.email,
          role: formData.role,
          expiration_date: formData.expiration_date || undefined,
        };
        if (formData.password) {
          createData.password = formData.password;
        }
        await adminAPI.createUser(createData);
        setSnackbar({
          open: true,
          message: 'User created successfully',
          severity: 'success',
        });
      }
      handleCloseDialog();
      fetchUsers();
    } catch (err: unknown) {
      const error = err as {
        response?: {
          data?: {
            detail?:
              | string
              | { message?: string; errors?: string[] }
              | Array<{ loc?: (string | number)[]; msg?: string }>;
          };
        };
      };
      const detail = error.response?.data?.detail;
      if (typeof detail === 'string') {
        setError(detail);
      } else if (Array.isArray(detail)) {
        const messages = detail
          .map((d) => {
            const field = d.loc?.[d.loc.length - 1];
            return field && d.msg ? `${field}: ${d.msg}` : d.msg;
          })
          .filter(Boolean);
        setError(messages.join('; ') || 'Failed to save user');
      } else if (detail && typeof detail === 'object' && 'message' in detail) {
        const suffix = detail.errors?.length ? `: ${detail.errors.join('; ')}` : '';
        setError(`${detail.message || 'Failed to save user'}${suffix}`);
      } else {
        setError('Failed to save user');
      }
    }
  };

  const handleDeleteUser = async (userId: string, username: string) => {
    if (!window.confirm(`Are you sure you want to delete user "${username}"?`)) {
      return;
    }
    try {
      await adminAPI.deleteUser(userId);
      setSnackbar({
        open: true,
        message: 'User deleted successfully',
        severity: 'success',
      });
      fetchUsers();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } };
      setSnackbar({
        open: true,
        message: error.response?.data?.detail || 'Failed to delete user',
        severity: 'error',
      });
    }
  };

  const handleGenerateInvite = async (userId: string) => {
    try {
      const result = await adminAPI.generateInvite(userId);
      setInviteUrl(result.invite_url);
      setInviteExpires(new Date(result.expires_at).toLocaleString());
      setInviteDialogOpen(true);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } };
      setSnackbar({
        open: true,
        message: error.response?.data?.detail || 'Failed to generate invite',
        severity: 'error',
      });
    }
  };

  const handleCopyInvite = () => {
    navigator.clipboard.writeText(inviteUrl);
    setSnackbar({
      open: true,
      message: 'Invite link copied to clipboard',
      severity: 'info',
    });
  };

  const handleResetPassword = async (userId: string, username: string) => {
    if (
      !window.confirm(
        `Reset password for "${username}"? They will receive a temporary password.`
      )
    ) {
      return;
    }
    try {
      const result = await adminAPI.resetPassword(userId);
      setSnackbar({
        open: true,
        message: `Temporary password: ${result.temporary_password}`,
        severity: 'info',
      });
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } };
      setSnackbar({
        open: true,
        message: error.response?.data?.detail || 'Failed to reset password',
        severity: 'error',
      });
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5" fontWeight={600}>
          User Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add User
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Username</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Password Set</TableCell>
                  <TableCell>Expires</TableCell>
                  <TableCell>Last Login</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id} hover>
                    <TableCell>{user.username}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <Chip
                        label={user.role}
                        color={user.role === 'admin' ? 'primary' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.is_active ? 'Active' : 'Inactive'}
                        color={user.is_active ? 'success' : 'error'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.is_password_set ? 'Yes' : 'No'}
                        color={user.is_password_set ? 'success' : 'warning'}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{formatDate(user.expiration_date)}</TableCell>
                    <TableCell>{formatDate(user.last_login)}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(user)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {!user.is_password_set && (
                        <Tooltip title="Generate Invite Link">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleGenerateInvite(user.id)}
                          >
                            <LinkIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {user.is_password_set && (
                        <Tooltip title="Reset Password">
                          <IconButton
                            size="small"
                            onClick={() =>
                              handleResetPassword(user.id, user.username)
                            }
                          >
                            <KeyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteUser(user.id, user.username)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {users.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        No users found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </Paper>
      )}

      {/* Create/Edit User Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingUser ? 'Edit User' : 'Add New User'}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <TextField
            fullWidth
            label="Username"
            value={formData.username}
            onChange={(e) =>
              setFormData({ ...formData, username: e.target.value })
            }
            margin="normal"
            disabled={!!editingUser}
            required
          />
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={formData.email}
            onChange={(e) =>
              setFormData({ ...formData, email: e.target.value })
            }
            margin="normal"
            required
          />
          {!editingUser && (
            <TextField
              fullWidth
              label="Password (optional)"
              type="password"
              value={formData.password}
              onChange={(e) =>
                setFormData({ ...formData, password: e.target.value })
              }
              margin="normal"
              error={formData.password.length > 0 && formData.password.length < 12}
              helperText="Min 12 characters incl. uppercase, lowercase, digit and special character. Leave empty to send invite link instead"
            />
          )}
          <FormControl fullWidth margin="normal">
            <InputLabel>Role</InputLabel>
            <Select
              value={formData.role}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  role: e.target.value as 'admin' | 'user',
                })
              }
              label="Role"
            >
              <MenuItem value="user">User</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
            </Select>
          </FormControl>
          {editingUser && (
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) =>
                    setFormData({ ...formData, is_active: e.target.checked })
                  }
                />
              }
              label="Active"
              sx={{ mt: 1 }}
            />
          )}
          <TextField
            fullWidth
            label="Expiration Date (optional)"
            type="datetime-local"
            value={formData.expiration_date}
            onChange={(e) =>
              setFormData({ ...formData, expiration_date: e.target.value })
            }
            margin="normal"
            InputLabelProps={{ shrink: true }}
            helperText="Leave empty for no expiration"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSaveUser} variant="contained">
            {editingUser ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Invite Link Dialog */}
      <Dialog
        open={inviteDialogOpen}
        onClose={() => setInviteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Invite Link Generated</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Share this link with the user to complete their registration:
          </Typography>
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              mt: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              backgroundColor: 'grey.50',
            }}
          >
            <Typography
              variant="body2"
              sx={{
                flex: 1,
                wordBreak: 'break-all',
                fontFamily: 'monospace',
              }}
            >
              {inviteUrl}
            </Typography>
            <Tooltip title="Copy to clipboard">
              <IconButton onClick={handleCopyInvite} size="small">
                <CopyIcon />
              </IconButton>
            </Tooltip>
          </Paper>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
            This link expires: {inviteExpires}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteDialogOpen(false)}>Close</Button>
          <Button onClick={handleCopyInvite} variant="contained" startIcon={<CopyIcon />}>
            Copy Link
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminUsersPage;
